import { MouseEvent, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { ChevronDownIcon, CloseIcon, PauseIcon, TrashIcon } from '@braskai/icons';
import { Button, Icon, Menu, ModalWrapper, styledOmitCustomProps, Typography } from '@braskai/ui-kit';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';

import { Box, CircularProgress, Portal, Stack } from '@mui/material';
import { alpha, css, styled } from '@mui/material/styles';

import {
  RecordingIntroVariant,
  useRecordingIntroGate,
  useRecordingIntroStore,
} from '@/features/recording/recordingIntro';
import {
  RecordingMinutesPaywallModal,
  useIsOutOfRecordingMinutes,
  useRecordingMaxDurationSeconds,
  useRecordingMinutesPaywallStore,
} from '@/features/recording/recordingPaywall';

import { formatRecordingDuration, useScreenRecordModalStore } from '@/entities/recording';
import { useRecordingEntitlement } from '@/entities/user/currentUser';

import { PATHS } from '@/shared/constants/routes/paths';
import { useInterfaceLanguage } from '@/shared/i18n';

import { CameraPlaceholderIcon, MicIcon, MicOffIcon, PlayIcon, VideoIcon, VideoOffIcon } from './icons';
import { RECORDER_Z_INDEX } from './lib/zIndex';
import { RecorderControlTooltip } from './RecorderControlTooltip';
import { useAppScreenRecorder } from './useAppScreenRecorder';

const SBackdrop = styled(
  Box,
  styledOmitCustomProps,
)<{ $blocking: boolean }>(
  ({ $blocking }) => css`
    position: fixed;
    inset: 0;
    z-index: ${RECORDER_Z_INDEX};
    pointer-events: ${$blocking ? 'auto' : 'none'};
  `,
);

const NARROW_RECORDER_MEDIA_QUERY = '@media (max-width: 900px)';

const SETUP_BAR_BOTTOM_PX = 30;
const SETUP_BAR_HEIGHT_PX = 76;
const ABOVE_BAR_GAP_PX = 12;
const RECORDING_BAR_TOP_PX = 20 + 56;

const SStage = styled(
  Box,
  styledOmitCustomProps,
)<{ $dimmed: boolean; $inset: number }>(
  ({ $dimmed, $inset, theme }) => css`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: ${$inset}px;
    pointer-events: none;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background-color: ${$dimmed ? alpha(theme.palette.grey[900], 0.16) : 'transparent'};
      transition: background-color 0.2s ease;
    }

    ${NARROW_RECORDER_MEDIA_QUERY} {
      left: 0;
    }
  `,
);

const SIDEBAR_SELECTOR = '.MuiDrawer-docked > .MuiDrawer-paper';

const getSidebarInset = () => {
  const sidebar = document.querySelector<HTMLElement>(SIDEBAR_SELECTOR);
  if (!sidebar) return 0;

  const rect = sidebar.getBoundingClientRect();
  const isLeftRail = rect.left <= 0 && rect.width > 0 && rect.right < window.innerWidth / 2;

  return isLeftRail ? Math.round(rect.right) : 0;
};

const useSidebarInset = () => {
  const { pathname } = useLocation();
  const [inset, setInset] = useState(getSidebarInset);

  useEffect(() => {
    const update = () => setInset(getSidebarInset());
    const sidebar = document.querySelector(SIDEBAR_SELECTOR);
    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);

    update();
    if (sidebar) resizeObserver?.observe(sidebar);
    window.addEventListener('resize', update);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [pathname]);

  return inset;
};

const SCameraCircle = styled(
  Box,
  styledOmitCustomProps,
)<{ $interactive: boolean }>(
  ({ $interactive, theme }) => css`
    position: absolute;
    right: 40px;
    bottom: 132px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 168px;
    height: 168px;
    overflow: hidden;
    color: ${theme.palette.grey[400]};
    background-color: ${theme.palette.grey[200]};
    border-radius: 50%;
    box-shadow: 0 18px 44px -12px ${alpha(theme.palette.grey[900], 0.42)};
    pointer-events: ${$interactive ? 'auto' : 'none'};

    ${NARROW_RECORDER_MEDIA_QUERY} {
      right: 16px;
      bottom: 230px;
      width: 118px;
      height: 118px;
    }
  `,
);

const SControlBar = styled(Box)(
  ({ theme }) => css`
    position: absolute;
    bottom: 30px;
    left: 50%;
    display: flex;
    align-items: center;
    gap: 8px;
    max-width: calc(100vw - 40px);
    padding: 12px;
    overflow-x: auto;
    background-color: ${theme.palette.common.white};
    border-radius: 26px;
    box-shadow:
      0 16px 48px -10px ${alpha(theme.palette.grey[900], 0.3)},
      0 2px 8px ${alpha(theme.palette.grey[900], 0.08)};
    white-space: nowrap;
    pointer-events: auto;
    transform: translateX(-50%);

    ${NARROW_RECORDER_MEDIA_QUERY} {
      right: 12px;
      left: 12px;
      flex-wrap: wrap;
      justify-content: center;
      width: fit-content;
      max-width: none;
      margin: 0 auto;
      overflow-x: visible;
      transform: none;
    }
  `,
);

const SCloseButton = styled('button')(
  ({ theme }) => css`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 0;
    color: ${theme.palette.grey[600]};
    background-color: ${theme.palette.grey[100]};
    border: 0;
    border-radius: 50%;
    cursor: pointer;

    &:hover {
      background-color: ${theme.palette.grey[200]};
    }
  `,
);

const SDeviceButton = styled('button')(
  ({ theme }) => css`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 10px;
    height: 52px;
    padding: 0 16px;
    color: ${theme.palette.grey[800]};
    font-weight: 500;
    font-size: 15px;
    font-family: inherit;
    line-height: 20px;
    background: transparent;
    border: 0;
    border-radius: 14px;
    cursor: pointer;

    &:hover:not(:disabled) {
      background-color: ${theme.palette.grey[50]};
    }

    &:disabled {
      cursor: default;
      opacity: 0.5;
    }
  `,
);

const SDeviceLabel = styled('span')`
  max-width: 110px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const SRecordButton = styled('button')(
  ({ theme }) => css`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 10px;
    height: 52px;
    padding: 0 22px;
    color: ${theme.palette.common.white};
    font-weight: 600;
    font-size: 15px;
    font-family: inherit;
    line-height: 20px;
    background-color: ${theme.palette.grey[900]};
    border: 0;
    border-radius: 14px;
    cursor: pointer;

    &:disabled {
      cursor: default;
    }
  `,
);

const SRecordDot = styled('span')`
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  background-color: #f5333f;
  border-radius: 50%;
`;

const SRecordingBar = styled(Box)(
  ({ theme }) => css`
    position: absolute;
    bottom: 20px;
    left: 50%;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    background-color: ${theme.palette.grey[900]};
    border-radius: 14px;
    box-shadow: ${theme.customShadows.md};
    white-space: nowrap;
    pointer-events: auto;
    transform: translateX(-50%);
  `,
);

const SStopButton = styled(Box)(
  ({ theme }) => css`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    padding: 4px;
    background-color: ${theme.palette.error[500]};
    border-radius: 8px;
    cursor: pointer;

    &:hover {
      background-color: ${theme.palette.error[600]};
    }
  `,
);

const SStopSquare = styled(Box)(
  ({ theme }) => css`
    width: 12px;
    height: 12px;
    background-color: ${theme.palette.common.white};
    border-radius: 2px;
  `,
);

const SIconButton = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }
`;

const SAboveBar = styled(
  Box,
  styledOmitCustomProps,
)<{ $bottom: number }>(
  ({ $bottom }) => css`
    position: absolute;
    bottom: ${$bottom}px;
    left: 50%;
    display: flex;
    flex-direction: column;
    align-items: center;
    pointer-events: none;
    transform: translateX(-50%);
  `,
);

const SStatusBanner = styled(Box)(
  ({ theme }) => css`
    min-width: 280px;
    max-width: calc(100vw - 48px);
    padding: 12px 20px;
    background-color: ${theme.palette.common.white};
    border: 1px solid ${theme.palette.grey[200]};
    border-radius: 12px;
    box-shadow: ${theme.customShadows.md};
    pointer-events: auto;
  `,
);

const SConfirmBackdrop = styled(Box)(
  ({ theme }) => css`
    position: fixed;
    inset: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${alpha(theme.palette.grey[900], 0.5)};
    pointer-events: auto;
  `,
);

const SConfirmCard = styled(Box)(
  ({ theme }) => css`
    width: 400px;
    max-width: calc(100vw - 48px);
    overflow: hidden;
    background-color: ${theme.palette.common.white};
    border-radius: 20px;
    box-shadow: ${theme.customShadows.xl};
  `,
);

const useSetupBarHeight = () => {
  const [bar, setBar] = useState<HTMLDivElement | null>(null);
  const [height, setHeight] = useState(SETUP_BAR_HEIGHT_PX);

  useLayoutEffect(() => {
    if (!bar || typeof ResizeObserver === 'undefined') return;

    const update = () => setHeight(Math.round(bar.getBoundingClientRect().height) || SETUP_BAR_HEIGHT_PX);
    const resizeObserver = new ResizeObserver(update);

    update();
    resizeObserver.observe(bar);

    return () => resizeObserver.disconnect();
  }, [bar]);

  return [height, setBar] as const;
};

const CameraPreview = ({ stream }: { stream: MediaStream | null }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current) videoRef.current.srcObject = stream;
  }, [stream]);

  return stream ? (
    <Box
      component="video"
      ref={videoRef}
      sx={{ height: '100%', objectFit: 'cover', transform: 'scaleX(-1)', width: '100%' }}
      autoPlay
      muted
      playsInline
    />
  ) : (
    <CameraPlaceholderIcon data-testid="screen-recorder-camera-placeholder" height={72} width={72} />
  );
};

const DeviceLabel = ({ children }: { children: string }) => (
  <Typography
    sx={{ maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    variant="body-2"
  >
    {children}
  </Typography>
);

const DeleteRecordingConfirmation = ({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: () => void }) => {
  const { t } = useInterfaceLanguage();

  return (
    <SConfirmBackdrop>
      <SConfirmCard aria-labelledby="delete-recording-title" role="alertdialog">
        <Stack gap="4px" sx={{ padding: '24px 24px 16px' }}>
          <Stack alignItems="flex-start" direction="row" gap="16px">
            <Typography id="delete-recording-title" color="grey.900" sx={{ flex: 1 }} variant="title-4-md">
              {t('recordingModals.recorder.delete.title')}
            </Typography>
            <SIconButton
              aria-label={t('recordingModals.recorder.delete.closeAria')}
              data-testid="screen-recorder-delete-close"
              role="button"
              onClick={onCancel}
            >
              <Icon color="grey.700" component={<CloseIcon />} size={24} />
            </SIconButton>
          </Stack>
          <Typography color="grey.500" variant="body-2">
            {t('recordingModals.recorder.delete.description')}
          </Typography>
        </Stack>
        <Stack direction="row" gap="12px" sx={{ padding: '0 24px 24px' }}>
          <Button data-testid="screen-recorder-delete-cancel" variant="outlined" fullWidth onClick={onCancel}>
            {t('recordingModals.recorder.delete.cancel')}
          </Button>
          <Button data-testid="screen-recorder-delete-confirm" variant="error" fullWidth onClick={onConfirm}>
            {t('recordingModals.recorder.delete.confirm')}
          </Button>
        </Stack>
      </SConfirmCard>
    </SConfirmBackdrop>
  );
};

const SPACE_ROUTE_PATTERN = `/${PATHS.spaces}/:spaceId/*`;

type DeviceMenu = 'camera' | 'microphone';

const ScreenRecordModalComponent = () => {
  const navigate = useNavigate();
  const spaceMatch = useMatch(SPACE_ROUTE_PATTERN);
  const [spaceId] = useState(spaceMatch?.params.spaceId ?? '');
  const { close } = useScreenRecordModalStore();
  const maxDurationSeconds = useRecordingMaxDurationSeconds(spaceId);
  const isOutOfRecordingMinutes = useIsOutOfRecordingMinutes(spaceId);
  const openRecordingMinutesPaywall = useRecordingMinutesPaywallStore((state) => state.open);
  const recorder = useAppScreenRecorder({ maxDurationSeconds, spaceId });
  const { t } = useInterfaceLanguage();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [openDeviceMenu, setOpenDeviceMenu] = useState<DeviceMenu | null>(null);
  const sidebarInset = useSidebarInset();
  const [setupBarHeight, setSetupBarRef] = useSetupBarHeight();
  const isRecording = ['paused', 'recording'].includes(recorder.status);
  const isFinishing = recorder.status === 'finishing';
  const isSaveFailed = recorder.status === 'saveFailed';
  const isDiscarding = recorder.status === 'discarding';
  const isSetup = !isRecording && !isFinishing && !isSaveFailed && !isDiscarding;
  // start() has already taken the device choices, so a change made while it runs would not apply to the recording.
  const isStarting = recorder.status === 'starting';
  const pauseResumeLabel =
    recorder.status === 'paused' ? t('recordingModals.recorder.resume') : t('recordingModals.recorder.pause');
  const selectedCamera = recorder.cameras.find((camera) => camera.deviceId === recorder.selectedCameraId);
  const selectedMicrophone = recorder.microphones.find(
    (microphone) => microphone.deviceId === recorder.selectedMicrophoneId,
  );

  useEffect(() => {
    if (!recorder.completedRecordingId) return;
    navigate(
      spaceId
        ? `/${PATHS.spaces}/${spaceId}/${PATHS.records}/${recorder.completedRecordingId}`
        : `/${PATHS.records}/${recorder.completedRecordingId}`,
    );
    close();
  }, [close, navigate, recorder.completedRecordingId, spaceId]);

  const handleClose = async () => {
    if (!['error', 'setup', 'starting'].includes(recorder.status)) return;
    await recorder.discard();
    close();
  };

  const handleRecord = () => {
    // The gate lets the recorder open without the space credits after a while; they may have arrived since.
    if (isOutOfRecordingMinutes) {
      close();
      openRecordingMinutesPaywall();
      return;
    }
    void recorder.start();
  };

  const getDeviceMenuProps = (menu: DeviceMenu) => ({
    open: openDeviceMenu === menu && !isStarting,
    handleOpen: (open: boolean) => setOpenDeviceMenu(open && !isStarting ? menu : null),
  });

  const isClosableByBackdrop = ['error', 'setup'].includes(recorder.status);
  const handleBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!isClosableByBackdrop || event.target !== event.currentTarget) return;
    void handleClose();
  };

  // No retry can save a recording once the space has no free recording minutes left (another tab used them, or the
  // credits arrived after the recorder opened): it is dropped and the paywall takes its place.
  const isOutOfRecordingMinutesOnSave = recorder.saveBlocker === 'outOfRecordingMinutes';
  const { discard } = recorder;
  useEffect(() => {
    if (!isOutOfRecordingMinutesOnSave) return;
    void discard().then(() => {
      close();
      openRecordingMinutesPaywall();
    });
  }, [close, discard, isOutOfRecordingMinutesOnSave, openRecordingMinutesPaywall]);

  const handleDelete = async () => {
    setShowDeleteConfirmation(false);
    await recorder.discard();
    close();
  };

  const cameraOptions = [
    ...recorder.cameras.map((camera, index) => ({
      id: camera.deviceId,
      label: (
        <DeviceLabel>{camera.label || t('recordingModals.recorder.cameraFallback', { index: index + 1 })}</DeviceLabel>
      ),
      action: () => {
        recorder.setSelectedCameraId(camera.deviceId);
        recorder.setCameraEnabled(true);
      },
      isActive: recorder.cameraEnabled && recorder.selectedCameraId === camera.deviceId,
    })),
    {
      id: 'no-camera',
      label: <DeviceLabel>{t('recordingModals.recorder.noCameraOption')}</DeviceLabel>,
      action: () => recorder.setCameraEnabled(false),
      isActive: !recorder.cameraEnabled,
    },
  ];

  const microphoneOptions = [
    ...recorder.microphones.map((microphone, index) => ({
      id: microphone.deviceId,
      label: (
        <DeviceLabel>
          {microphone.label || t('recordingModals.recorder.microphoneFallback', { index: index + 1 })}
        </DeviceLabel>
      ),
      action: () => {
        recorder.setSelectedMicrophoneId(microphone.deviceId);
        recorder.setMicrophoneEnabled(true);
      },
      isActive: recorder.microphoneEnabled && recorder.selectedMicrophoneId === microphone.deviceId,
    })),
    {
      id: 'no-microphone',
      label: <DeviceLabel>{t('recordingModals.recorder.noMicrophoneOption')}</DeviceLabel>,
      action: () => recorder.setMicrophoneEnabled(false),
      isActive: !recorder.microphoneEnabled,
    },
  ];

  return (
    <Portal>
      <SBackdrop
        $blocking={isClosableByBackdrop}
        aria-label={t('recordingModals.recorder.ariaLabel')}
        data-testid="screen-record-modal-backdrop"
        role="dialog"
        onClick={handleBackdropClick}
      >
        <SStage $dimmed={isSetup} $inset={sidebarInset} data-testid="screen-record-modal-stage">
          {recorder.cameraEnabled && (
            <SCameraCircle $interactive={isClosableByBackdrop} data-testid="screen-record-modal-camera-bubble">
              <CameraPreview stream={recorder.previewStream} />
            </SCameraCircle>
          )}

          {showDeleteConfirmation && (
            <DeleteRecordingConfirmation onCancel={() => setShowDeleteConfirmation(false)} onConfirm={handleDelete} />
          )}

          {recorder.error && (
            <SAboveBar
              $bottom={(isSetup ? SETUP_BAR_BOTTOM_PX + setupBarHeight : RECORDING_BAR_TOP_PX) + ABOVE_BAR_GAP_PX}
              data-testid="screen-record-modal-above-bar"
            >
              <SStatusBanner>
                <Typography color="error.700" variant="body-2">
                  {recorder.error}
                </Typography>
              </SStatusBanner>
            </SAboveBar>
          )}

          {isFinishing && (
            <SRecordingBar data-testid="app-screen-recorder-controls">
              <CircularProgress size={20} sx={{ color: 'common.white' }} />
              <Typography color="common.white" role="status" variant="body-2-md">
                {recorder.isReconnecting
                  ? t('recordingModals.recorder.reconnecting')
                  : t('recordingModals.recorder.saving')}
              </Typography>
            </SRecordingBar>
          )}

          {isDiscarding && (
            <SRecordingBar data-testid="app-screen-recorder-controls">
              <CircularProgress size={20} sx={{ color: 'common.white' }} />
            </SRecordingBar>
          )}

          {isSaveFailed && (
            <SRecordingBar data-testid="app-screen-recorder-controls">
              <Typography color="common.white" role="alert" variant="body-2-md">
                {t('recordingModals.recorder.saveFailed.title')}
              </Typography>
              {!recorder.saveBlocker && (
                <Button
                  data-testid="app-screen-recorder-retry-save"
                  size="sm"
                  variant="primary"
                  onClick={() => void recorder.retrySave()}
                >
                  {t('recordingModals.recorder.saveFailed.retry')}
                </Button>
              )}
              <Button
                data-testid="app-screen-recorder-delete"
                size="sm"
                variant="error"
                onClick={() => setShowDeleteConfirmation(true)}
              >
                {t('recordingModals.recorder.saveFailed.delete')}
              </Button>
            </SRecordingBar>
          )}

          {isRecording && (
            <SRecordingBar data-testid="app-screen-recorder-controls">
              <RecorderControlTooltip title={t('recordingModals.recorder.stop')}>
                <SStopButton
                  aria-label={t('recordingModals.recorder.stop')}
                  data-testid="app-screen-recorder-stop"
                  role="button"
                  onClick={recorder.stop}
                >
                  <SStopSquare />
                </SStopButton>
              </RecorderControlTooltip>
              <Typography
                sx={{
                  fontVariantNumeric: 'tabular-nums',
                  minWidth: '8ch',
                  opacity: recorder.status === 'paused' ? 0.5 : 1,
                }}
                aria-label={t('recordingModals.recorder.duration')}
                color="common.white"
                role="timer"
                textAlign="center"
                variant="body-2-md"
              >
                {formatRecordingDuration(recorder.durationSeconds)}
                {maxDurationSeconds !== undefined && ` / ${formatRecordingDuration(maxDurationSeconds)}`}
              </Typography>
              {recorder.isReconnecting && (
                <Stack alignItems="center" data-testid="app-screen-recorder-reconnecting" direction="row" gap="6px">
                  <CircularProgress size={14} sx={{ color: 'warning.300' }} />
                  <Typography color="warning.300" role="status" variant="body-2-md">
                    {t('recordingModals.recorder.reconnecting')}
                  </Typography>
                </Stack>
              )}
              <RecorderControlTooltip title={pauseResumeLabel}>
                <SIconButton
                  aria-label={pauseResumeLabel}
                  data-testid="app-screen-recorder-pause-resume"
                  role="button"
                  onClick={recorder.status === 'paused' ? recorder.resume : recorder.pause}
                >
                  <Icon
                    color="common.white"
                    component={recorder.status === 'paused' ? <PlayIcon /> : <PauseIcon />}
                    size={24}
                  />
                </SIconButton>
              </RecorderControlTooltip>
              <RecorderControlTooltip title={t('recordingModals.recorder.discard')}>
                <SIconButton
                  aria-label={t('recordingModals.recorder.discard')}
                  data-testid="app-screen-recorder-discard"
                  role="button"
                  onClick={() => setShowDeleteConfirmation(true)}
                >
                  <Icon color="common.white" component={<TrashIcon />} size={24} />
                </SIconButton>
              </RecorderControlTooltip>
            </SRecordingBar>
          )}

          {isSetup && (
            <SControlBar data-testid="screen-record-modal-setup-bar" ref={setSetupBarRef}>
              <RecorderControlTooltip title={t('recordingModals.recorder.close')}>
                <SCloseButton
                  aria-label={t('recordingModals.recorder.close')}
                  data-testid="screen-record-modal-cancel"
                  type="button"
                  onClick={handleClose}
                >
                  <CloseIcon height={18} width={18} />
                </SCloseButton>
              </RecorderControlTooltip>

              <RecorderControlTooltip title={t('recordingModals.recorder.camera')} hideOnClick>
                <Menu
                  {...getDeviceMenuProps('camera')}
                  disablePortal={false}
                  options={cameraOptions}
                  placement="top-start"
                  width={240}
                  zIndexPopover={RECORDER_Z_INDEX}
                >
                  <SDeviceButton data-testid="screen-record-modal-camera" disabled={isStarting} type="button">
                    <Icon
                      color={recorder.cameraEnabled ? 'inherit' : 'error.500'}
                      component={recorder.cameraEnabled ? <VideoIcon /> : <VideoOffIcon />}
                      size={22}
                    />
                    <SDeviceLabel>
                      {recorder.cameraEnabled
                        ? selectedCamera?.label || t('recordingModals.recorder.camera')
                        : t('recordingModals.recorder.noCamera')}
                    </SDeviceLabel>
                    <Icon color="grey.400" component={<ChevronDownIcon />} size={16} />
                  </SDeviceButton>
                </Menu>
              </RecorderControlTooltip>

              <RecorderControlTooltip title={t('recordingModals.recorder.microphone')} hideOnClick>
                <Menu
                  {...getDeviceMenuProps('microphone')}
                  disablePortal={false}
                  options={microphoneOptions}
                  placement="top-start"
                  width={240}
                  zIndexPopover={RECORDER_Z_INDEX}
                >
                  <SDeviceButton data-testid="screen-record-modal-microphone" disabled={isStarting} type="button">
                    <Icon
                      color={recorder.microphoneEnabled ? 'inherit' : 'error.500'}
                      component={recorder.microphoneEnabled ? <MicIcon /> : <MicOffIcon />}
                      size={22}
                    />
                    <SDeviceLabel>
                      {recorder.microphoneEnabled
                        ? selectedMicrophone?.label || t('recordingModals.recorder.microphone')
                        : t('recordingModals.recorder.noMicrophone')}
                    </SDeviceLabel>
                    <Icon color="grey.400" component={<ChevronDownIcon />} size={16} />
                  </SDeviceButton>
                </Menu>
              </RecorderControlTooltip>

              <SRecordButton
                data-testid="screen-record-modal-start-recording"
                disabled={isStarting}
                type="button"
                onClick={handleRecord}
              >
                {isStarting ? <CircularProgress size={14} sx={{ color: 'common.white' }} /> : <SRecordDot />}
                {t('recordingModals.recorder.record')}
              </SRecordButton>
            </SControlBar>
          )}
        </SStage>
      </SBackdrop>
    </Portal>
  );
};

const ENTITLEMENT_WAIT_MS = 3_000;

/**
 * Decides what a "new recording" opens, in order: the out-of-minutes paywall; else, once per user, the
 * first-recording intro (trial and paid spaces), which opens the recorder itself on "Record video"; else the
 * recorder. The answer is taken once, when the paywall check settles: an intro flag still loading at that moment
 * opens the recorder (BaseLayout loads the flag early, so this is rare).
 * The extension prompt (useRecordingExtensionPromptGate) is not offered on a new recording for now.
 */
const ScreenRecordModalGate = () => {
  const entitlement = useRecordingEntitlement();
  const isEntitlementKnown = entitlement.status !== 'loading';
  const isOutOfRecordingMinutes = useIsOutOfRecordingMinutes();
  const introVariant = useRecordingIntroGate();
  const [isBlocked, setIsBlocked] = useState<boolean | null>(isEntitlementKnown ? isOutOfRecordingMinutes : null);
  // `undefined` until decided; null opens the recorder.
  const [intro, setIntro] = useState<null | RecordingIntroVariant | undefined>(
    isEntitlementKnown && !isOutOfRecordingMinutes ? introVariant : undefined,
  );
  const { close } = useScreenRecordModalStore();
  const openRecordingMinutesPaywall = useRecordingMinutesPaywallStore((state) => state.open);
  const openRecordingIntro = useRecordingIntroStore((state) => state.open);

  useEffect(() => {
    if (isBlocked !== null) return;
    if (isEntitlementKnown) {
      setIsBlocked(isOutOfRecordingMinutes);
      setIntro(isOutOfRecordingMinutes ? undefined : introVariant);
      return;
    }
    // Without an answer about the plan there is no intro either.
    const timer = window.setTimeout(() => {
      setIsBlocked(false);
      setIntro(null);
    }, ENTITLEMENT_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [introVariant, isBlocked, isEntitlementKnown, isOutOfRecordingMinutes]);

  useLayoutEffect(() => {
    if (!isBlocked) return;
    close();
    openRecordingMinutesPaywall();
  }, [close, isBlocked, openRecordingMinutesPaywall]);

  useLayoutEffect(() => {
    if (!intro) return;
    close();
    openRecordingIntro({ variant: intro });
  }, [close, intro, openRecordingIntro]);

  return isBlocked === false && intro === null ? <ScreenRecordModalComponent /> : null;
};

export const ScreenRecordModal = () => (
  <>
    <ModalWrapper hook={useScreenRecordModalStore}>
      <ScreenRecordModalGate />
    </ModalWrapper>
    <RecordingMinutesPaywallModal />
  </>
);
